/**
 * Unrecognised comments: the operator end of the C-tier improvement loop.
 *
 * The hub saves a comment here whenever the no-LLM gate abstained — it could
 * not recognise the text. Each row is one distinct phrasing; repeats bump
 * `seenCount` rather than adding rows, so the list is ordered by how often
 * customers actually said it.
 *
 * Two outcomes, both of which delete the row:
 *   - **Add as phrase** — appends the text to an existing intent via
 *     `POST /api/intents/:id/texts`, which owns embedding + Qdrant sync.
 *   - **Dismiss** — junk, spam, or something that will never be an intent.
 *
 * Deleting on handling is what keeps the table small; the 90-day `lastSeenAt`
 * expiry is only the backstop for rows nobody ever looks at.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { hubClient, type UnrecognizedComment } from '../../services/hubClient';
import { hubService, type Intent } from '../../services/hub.service';

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.round((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The miss has two shapes and they need different fixes, so the row says which.
 * Candidates present means retrieval found something close — the phrasing
 * belongs on an intent that already exists. Nothing retrieved means the intent
 * itself is missing and needs creating first.
 */
export function MissShape({ row }: { row: Pick<UnrecognizedComment, 'candidateCodes' | 'topScore'> }) {
  const near = (row.candidateCodes?.length ?? 0) > 0;
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[11px] ${
        near ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
      }`}
      title={
        near
          ? `Closest intents: ${row.candidateCodes?.join(', ')}`
          : 'Nothing was retrieved — this may need a new intent'
      }
    >
      {near ? `near miss · ${row.topScore?.toFixed(2) ?? '—'}` : 'no match'}
    </span>
  );
}

export interface UnrecognizedCommentsTabProps {
  system: string;
  botId: string;
}

export function UnrecognizedCommentsTab({ system, botId }: UnrecognizedCommentsTabProps) {
  const { getToken } = useAuth();
  const [items, setItems] = useState<UnrecognizedComment[]>([]);
  const [intents, setIntents] = useState<Intent[]>([]);
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, ints] = await Promise.all([
        hubClient.listUnrecognizedComments({ system, botId, limit: 200 }),
        hubService.listIntentsByBot(botId, system, getToken()).catch(() => [] as Intent[]),
      ]);
      setItems(rows);
      setIntents(ints);
      // Preselect the gate's own top candidate. This is the payoff for storing
      // candidateCodes: the common case (a phrasing that belongs on an intent
      // the bot already has) becomes one click instead of a hunt through a
      // dropdown the operator has to reason about.
      const codeToIntent = new Map(ints.map((i) => [i.intentCode, i.id]));
      setChosen((prev) => {
        const next = { ...prev };
        for (const r of rows) {
          if (next[r.id]) continue;
          const hit = r.candidateCodes?.find((c) => codeToIntent.has(c));
          if (hit) next[r.id] = codeToIntent.get(hit)!;
        }
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [system, botId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  async function addAsPhrase(row: UnrecognizedComment) {
    const intentId = chosen[row.id];
    if (!intentId) return;
    setBusyId(row.id);
    setError(null);
    setNotice(null);
    try {
      await hubService.createIntentText(intentId, row.text, getToken(), 'review');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add the training phrase');
      setBusyId(null);
      return;
    }
    // The phrase is saved. If the row delete fails the phrase still exists, so
    // say so explicitly — silently reloading would leave the operator to add it
    // a second time on the next pass.
    try {
      await hubClient.deleteUnrecognizedComment(row.id);
      const code = intents.find((i) => i.id === intentId)?.intentCode ?? 'intent';
      setNotice(`Added to ${code} and removed from the list.`);
    } catch (e) {
      setError(
        `Phrase was added, but removing the row failed (${
          e instanceof Error ? e.message : 'unknown error'
        }). Dismiss it manually so it is not added twice.`,
      );
    }
    setBusyId(null);
    await load();
  }

  async function dismiss(row: UnrecognizedComment) {
    setBusyId(row.id);
    setError(null);
    setNotice(null);
    try {
      await hubClient.deleteUnrecognizedComment(row.id);
      setNotice('Dismissed.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to dismiss');
    } finally {
      setBusyId(null);
    }
  }

  async function prune() {
    if (!confirm('Delete every comment not seen in the last 90 days? This cannot be undone.'))
      return;
    setError(null);
    try {
      const res = await hubClient.pruneUnrecognizedComments();
      setNotice(`Expiry sweep removed ${res.deleted} row(s) older than ${res.ttlDays} days.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to prune');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          Comments the no-LLM gate could not recognise, most repeated first. Adding one as a
          training phrase teaches the intent; dismissing drops it. Either way the row goes away.
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => void load()}
            disabled={loading}
            className="rounded border border-gray-300 px-3 py-1 text-xs disabled:opacity-50"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            onClick={() => void prune()}
            className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-600"
            title="Runs the same expiry sweep the nightly job runs"
          >
            Run expiry sweep
          </button>
        </div>
      </div>

      {error && <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {notice && <div className="rounded-lg bg-green-50 p-3 text-sm text-green-800">{notice}</div>}
      {intents.length === 0 && !loading && items.length > 0 && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
          This bot has no intents yet, so there is nothing to add a phrase to. Create an intent
          first — these comments are the evidence for which ones are missing.
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Seen', 'Comment', 'Miss', 'Last seen', 'Add to intent'].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((row) => (
              <tr key={row.id} className={busyId === row.id ? 'opacity-50' : undefined}>
                <td className="px-3 py-2 align-top">
                  <span
                    className="rounded bg-blue-100 px-1.5 py-0.5 text-[11px] font-medium text-blue-800"
                    title={`First seen ${new Date(row.firstSeenAt).toLocaleString()}`}
                  >
                    {row.seenCount}×
                  </span>
                </td>
                <td className="max-w-md px-3 py-2 align-top">
                  <div className="whitespace-pre-wrap break-words text-gray-900">{row.text}</div>
                  {row.channel && (
                    <div className="mt-0.5 text-[11px] text-gray-400">{row.channel}</div>
                  )}
                </td>
                <td className="px-3 py-2 align-top">
                  <MissShape row={row} />
                </td>
                <td className="px-3 py-2 align-top whitespace-nowrap text-xs text-gray-500">
                  {relativeTime(row.lastSeenAt)}
                </td>
                <td className="px-3 py-2 align-top">
                  <div className="flex items-center gap-2">
                    <select
                      value={chosen[row.id] ?? ''}
                      onChange={(e) => setChosen((p) => ({ ...p, [row.id]: e.target.value }))}
                      className="max-w-[200px] rounded border border-gray-300 px-2 py-1 text-xs"
                    >
                      <option value="">choose intent…</option>
                      {intents.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.intentCode}
                          {i.name ? ` — ${i.name}` : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => void addAsPhrase(row)}
                      disabled={!chosen[row.id] || busyId === row.id}
                      className="rounded bg-blue-600 px-3 py-1 text-xs text-white disabled:opacity-40"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => void dismiss(row)}
                      disabled={busyId === row.id}
                      className="rounded border border-gray-300 px-3 py-1 text-xs disabled:opacity-40"
                    >
                      Dismiss
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-400">
                  Nothing unrecognised. Either the gate is answering everything, or no traffic has
                  reached it yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UnrecognizedCommentsTab;
